import { maxBy, minBy, uniq } from 'es-toolkit';
import React from 'react';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import { produce } from 'immer';

import { PropertySelectField } from '@paragon/client-tools';
import { AtticSettings as AtticSettingsInterface } from '@paragon/contract-library';
import { Direction2D } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { getLumberDimension, lumberCanBeUsedForComponentThickness } from '@paragon/material-lists';
import { KeyToValueOfType, notNullish, pairwise } from '@paragon/utilities';

import { DEFAULT_PROFILE_TRUSS_THICKNESS } from '../../../../library/layout-geometry/truss-envelopes/defaults';
import { ProfileClass } from '../../../../library/profiles/ProfileClass';
import { getDefaultAtticSettings } from '../../../../library/profiles/profileTrussDefaults';
import { updateActiveAtticSettings } from '../../../../store/actions/profileActions';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { getUniqueLumbersInLumberListForActiveComponentDesign } from '../../../../store/selectors/getUniqueLumbersInLumberList';
import {
  getComponentDesignBeingInserted,
  getComponentDesignInsertionInfoOrNull,
} from '../../../../store/selectors/insertionObjectSelectors';
import {
  getInsertingProfileClassOrNull,
  getInsertingProfileTrussSettingsOrNull,
} from '../../../../store/selectors/profileSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SingleZoneSettingsPanel } from './ZoneSettings';

export const InsertionProfileAtticSettings = () => {
  const dispatch = useAppDispatch();
  const profileTrussSettings = useAppSelector(getInsertingProfileTrussSettingsOrNull);
  const profileClassResult = useAppSelector(getInsertingProfileClassOrNull);
  const profile = useAppSelector(getComponentDesignBeingInserted)?.profile ?? null;
  const atticSettings = profileTrussSettings && profileTrussSettings.profile.atticSettings;
  return (
    <AtticSettings
      settings={atticSettings}
      onChange={async (settings) => await dispatch(updateActiveAtticSettings(settings))}
      span={profileTrussSettings?.span ?? 0}
      disabled={!profile}
      centerOnPeakButton={{ profileClass: profileClassResult?.type === 'Ok' ? profileClassResult.ok : null }}
    />
  );
};

export const AtticSettings = ({
  settings: atticSettings,
  span,
  centerOnPeakButton,
  onChange,
  disabled,
  disallowNull,
}: {
  settings: AtticSettingsInterface | null;
  span: number;
  centerOnPeakButton: false | { profileClass: ProfileClass | null };
  onChange: (newSettings: AtticSettingsInterface | null) => void | Promise<void>;
  disabled?: boolean;
  disallowNull?: boolean;
}) => {
  const insertionInfo = useAppSelector(getComponentDesignInsertionInfoOrNull);

  const inventoryLumbers = useAppSelector(getUniqueLumbersInLumberListForActiveComponentDesign);
  const lumberWidths = uniq(
    inventoryLumbers
      .filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, DEFAULT_PROFILE_TRUSS_THICKNESS))
      .map((lumber) => getLumberDimension(lumber, DEFAULT_PROFILE_TRUSS_THICKNESS)),
  ).toSorted((a, b) => a - b);

  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  if (insertionInfo == null || insertionInfo.profileDefaults == null) return null;

  const toggleAttic = async () => {
    if (disallowNull && atticSettings != null) return;
    return await onChange(atticSettings ? null : getDefaultAtticSettings(insertionInfo.profileDefaults!));
  };

  const toggleUseSingleFloorChord = async () => {
    const useSingleFloorChord = !atticSettings!.useSingleFloorChord;
    const floorDepthOverride: Partial<AtticSettingsInterface> = {
      floorDepth: useSingleFloorChord ? (lumberWidths[0] ?? 0) : insertionInfo.profileDefaults!.atticFloorDepth,
    };
    return await onChange({ ...atticSettings!, useSingleFloorChord, ...floorDepthOverride });
  };
  const setDistance = (key: KeyToValueOfType<AtticSettingsInterface, number>) => async (value: number) =>
    await onChange({ ...atticSettings!, [key]: value });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-around' }}>
        <FormControlLabel
          sx={{ m: 1 }}
          control={<Checkbox checked={!!atticSettings} onChange={toggleAttic} />}
          label="Attic"
          disabled={disabled || (disallowNull && atticSettings != null)}
        />
        <FormControlLabel
          sx={{ m: 1 }}
          control={<Checkbox checked={atticSettings?.useSingleFloorChord} onChange={toggleUseSingleFloorChord} />}
          label="Single-Beam Floor"
          disabled={disabled || !atticSettings}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-around' }}>
        <DistanceField
          label="Room Center"
          value={atticSettings?.middle ?? 0}
          onSubmit={setDistance('middle')}
          disabled={disabled || !atticSettings}
          sx={{ m: 1, width: '100%' }}
        />
        <Button
          sx={{ m: 1, width: '100%' }}
          variant="outlined"
          disabled={disabled || !atticSettings}
          onClick={async () => await setDistance('middle')(span / 2)}
        >
          Center On Span
        </Button>
        {centerOnPeakButton ? (
          <Button
            sx={{ m: 1, width: '100%' }}
            variant="outlined"
            disabled={disabled || !atticSettings || !centerOnPeakButton.profileClass?.isClosed}
            onClick={async () =>
              centerOnPeakButton.profileClass &&
              (await setDistance('middle')(getAtticPeakX(centerOnPeakButton.profileClass)))
            }
          >
            Center On Peak
          </Button>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-around' }}>
        <DistanceField
          label="Room Width"
          value={atticSettings?.width ?? 0}
          min={0}
          onSubmit={async (newWidth) =>
            await onChange(
              produce(atticSettings!, (draft) => {
                draft.width = newWidth;
                draft.zoneSettings.horizontalWebLength = getAtticFloorWebLength(newWidth, draft.zoneSettings.maxWebRun);
              }),
            )
          }
          disabled={!atticSettings}
          sx={{ m: 1, width: '100%' }}
        />
        <DistanceField
          label="Room Height"
          value={atticSettings?.height ?? 0}
          min={0}
          onSubmit={setDistance('height')}
          disabled={!atticSettings}
          sx={{ m: 1, width: '100%' }}
        />
        {atticSettings?.useSingleFloorChord ? (
          <PropertySelectField<number>
            property="Floor Depth"
            value={atticSettings?.floorDepth ?? 0}
            options={lumberWidths}
            submit={setDistance('floorDepth')}
            getName={distanceFormatter}
            disabled={!atticSettings}
            sx={{ m: 1, width: '100%' }}
          />
        ) : (
          <DistanceField
            label="Floor Depth"
            value={atticSettings?.floorDepth ?? 0}
            min={0}
            onSubmit={setDistance('floorDepth')}
            disabled={!atticSettings}
            sx={{ m: 1, width: '100%' }}
          />
        )}
      </Box>
      <SingleZoneSettingsPanel
        value={atticSettings?.zoneSettings}
        onChange={async (newSettings) => await onChange({ ...atticSettings!, zoneSettings: newSettings })}
        disabled={!atticSettings}
        hideWebStart
      />
    </Box>
  );
};

/**
 * Calculates the x coordinate of the highest peak of the truss. Both real peaks and "virtual" peaks (where the profile
 * is truncated by a horizontal line) are considered.
 */
function getAtticPeakX(profile: ProfileClass): number {
  const slopedTopChordLines = profile.topChords
    .map(({ segment }) => segment.toLine())
    .filter((line) => !line.isParallelTo({ direction: Direction2D.left }));
  const peaks = pairwise(slopedTopChordLines)
    .filter(([previous, next]) => previous.direction.y > 0 && next.direction.y < 0)
    .map(([previous, next]) => previous.intersectWithLine(next))
    .filter(notNullish);
  const highestPeak = maxBy(peaks, ({ y }) => y);

  return highestPeak
    ? highestPeak.x
    : // Fall back to center of span
      profile.span / 2;
}

// there is a server-side version of this function used for default profile defaults; keep it in sync
export function getAtticFloorWebLength(width: number, webRun: number): number {
  const IDEAL_WEB_LENGTH = 15;
  const WEB_WIDTH = 3.5;

  let widthPlusVertical = width + WEB_WIDTH;
  // Preserve symmetry: find the ideal length to the center of the webbed area for odd-length web runs.
  if (webRun % 2 === 1) widthPlusVertical /= 2;

  const idealRunWidth = webRun * IDEAL_WEB_LENGTH + WEB_WIDTH;
  const idealRunCount = widthPlusVertical / idealRunWidth;

  const bestRunCountCandidates = [Math.floor(idealRunCount), Math.ceil(idealRunCount)];
  const bestWebLengthCandidates = bestRunCountCandidates
    .map((runCount) => {
      const runWidth = widthPlusVertical / runCount;
      const diagonalsWidth = runWidth - WEB_WIDTH;
      const singleWebLength = diagonalsWidth / webRun;
      return singleWebLength;
    })
    .filter((candidate) => Number.isFinite(candidate) && candidate > 0);

  // Find proposed length closest to the ideal
  return (
    minBy(
      bestWebLengthCandidates,
      (webLength) => Math.max(webLength, IDEAL_WEB_LENGTH) / Math.min(webLength, IDEAL_WEB_LENGTH),
    ) ?? IDEAL_WEB_LENGTH
  );
}
